export const SHARED_CONTENT_PARAM = "share";
export const SHARED_CONTENT_ITEM_PARAM = "item";
export const SHARED_CONTENT_SECTION_PARAM = "section";
const APP_LINK_ORIGIN = "https://app-conectado.connectapps.com.br";

const APP_PATH_BY_KIND: Record<string, string> = {
  conteudos: "conteudo",
  destaques: "conteudo",
  "biblioteca-multimidia": "conteudo",
  "biblioteca-documentos": "conteudo",
  "biblioteca-games": "conteudo",
  cursos: "curso",
  trilhas: "trilha",
  "treinamentos-de-1-ponto": "treinamento-1-ponto",
  comunicados: "comunicado",
  "seguranca-do-trabalho": "segurancaDoTrabalho",
};

export const buildAppContentUrl = (
  kind: string,
  id: number,
  providedUrl?: string
) => {
  if (providedUrl?.trim()) return providedUrl.trim();

  const path = APP_PATH_BY_KIND[kind];
  if (!path || !Number.isSafeInteger(id) || id <= 0) return undefined;

  return `${APP_LINK_ORIGIN}/${path}/${window.btoa(String(id))}`;
};

// Links do app (/<tipo>/<código>) funcionam no celular (abrem o app) e no computador (a página
// de instalação leva ao Portal do Aluno), então podem ser compartilhados sozinhos. Formatos
// desconhecidos continuam acompanhados do link do portal.
export const isSingleDeeplinkUrl = (appUrl: string) =>
  /^https?:\/\/[^/]+\/(?:conteudo|curso|trilha|comunicado|treinamento-1-ponto|segurancaDoTrabalho|story)\/[A-Za-z0-9_-]+(?:=|%3D){0,2}\/?$/i.test(
    appUrl
  );

// Rótulos usados pelo app na mensagem de compartilhar (ROTULOS_ACOES_V2 em tipoConteudo.ts).
// No app, destaques e itens das bibliotecas são conteúdos.
const APP_SHARE_SUBJECT_BY_KIND: Record<string, string> = {
  conteudos: "do Conteúdo",
  destaques: "do Conteúdo",
  "biblioteca-multimidia": "do Conteúdo",
  "biblioteca-documentos": "do Conteúdo",
  "biblioteca-games": "do Conteúdo",
  cursos: "do Curso",
  trilhas: "da Trilha",
  comunicados: "do Comunicado",
  "treinamentos-de-1-ponto": "do Treinamento de 1 Ponto",
  "seguranca-do-trabalho": "da Segurança do Trabalho",
};

export const getShareSubjectLabel = (kind: string) =>
  Object.prototype.hasOwnProperty.call(APP_SHARE_SUBJECT_BY_KIND, kind)
    ? APP_SHARE_SUBJECT_BY_KIND[kind]
    : "do Conteúdo";

// Mesmo texto do app (ActionModal): o nome vem entre aspas, como o app o envia.
export const buildContentShareMessage = (
  userName: string,
  subjectLabel: string,
  title: string,
  appUrl: string,
  portalUrl?: string
) => {
  const trimmedName = userName.trim();
  const name = trimmedName ? `"${trimmedName}"` : "Um usuário";

  if (portalUrl === undefined) {
    return `${name} está compartilhando com você o link ${subjectLabel} ${title}. Clique no link abaixo para acessá-lo.\n\n${appUrl}`;
  }

  const subject = subjectLabel.replace(/^(\S+\s+)(\S)/, (_, prefix, initial) =>
    `${prefix}${initial.toLocaleUpperCase("pt-BR")}`
  );
  return `${name} está compartilhando com você o link ${subject} ${title}. Você pode acessá-lo pelo app Conectado ou pelo Portal do Aluno.\n\nNo app Conectado:\n${appUrl}\n\nNo Portal do Aluno:\n${portalUrl}`;
};

interface SharedContentSelection {
  itemId?: number;
  sectionId?: string;
}

const PENDING_SHARED_ROUTE_KEY = "conectado:rota-compartilhada";

const isPortalRoute = (route: string) =>
  route === "/app" || route.startsWith("/app/") || route.startsWith("/app?");

export const buildSharedContentUrl = (
  route: string,
  selection: SharedContentSelection = {}
) => {
  const url = new URL(route, window.location.origin);
  url.searchParams.set(SHARED_CONTENT_PARAM, "1");
  url.searchParams.delete(SHARED_CONTENT_ITEM_PARAM);
  url.searchParams.delete(SHARED_CONTENT_SECTION_PARAM);

  if (
    selection.itemId &&
    Number.isSafeInteger(selection.itemId) &&
    selection.itemId > 0
  ) {
    url.searchParams.set(SHARED_CONTENT_ITEM_PARAM, String(selection.itemId));
    if (selection.sectionId) {
      url.searchParams.set(SHARED_CONTENT_SECTION_PARAM, selection.sectionId);
    }
  }

  return url.toString();
};

export const getSharedContentSelection = (
  search: string
): SharedContentSelection => {
  const params = new URLSearchParams(search);
  const rawItemId = params.get(SHARED_CONTENT_ITEM_PARAM);
  const itemMatch = rawItemId?.trim().match(/^(\d+)(?:\s|$)/);
  const itemId = itemMatch ? Number(itemMatch[1]) : undefined;

  return {
    itemId:
      itemId && Number.isSafeInteger(itemId) && itemId > 0 ? itemId : undefined,
    sectionId:
      params.get(SHARED_CONTENT_SECTION_PARAM)?.trim().split(/\s/)[0] || undefined,
  };
};

export const isSharedContentAccess = (search: string) =>
  new URLSearchParams(search).has(SHARED_CONTENT_PARAM);

export const savePendingSharedRoute = (pathname: string, search: string) => {
  if (!isPortalRoute(pathname) || !isSharedContentAccess(search)) return;

  try {
    localStorage.setItem(PENDING_SHARED_ROUTE_KEY, `${pathname}${search}`);
  } catch {
    return;
  }
};

export const takePendingSharedRoute = () => {
  try {
    const route = localStorage.getItem(PENDING_SHARED_ROUTE_KEY);
    localStorage.removeItem(PENDING_SHARED_ROUTE_KEY);

    return route && isPortalRoute(route) ? route : undefined;
  } catch {
    return undefined;
  }
};
